import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import _findIndex from 'lodash/findIndex';
import _isObject from 'lodash/isObject';
import _isString from 'lodash/isString';
import _omit from 'lodash/omit';
import _noop from 'lodash/noop';
import { bem } from 'helpers/class-name';
import { shouldShowError } from 'helpers/page-utils';
import { reduxFormPropTypes, intlMessage, intlShape } from 'constants/propTypes';
import { Dropdown, Menu } from 'components/Dropdown';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import InputError from '../InputError/InputError';
import './Autocomplete.scss';

const messages = defineMessages({
  noMatch: {
    id: 'no-match',
    defaultMessage: 'No matching options',
  },
});

const { block, element } = bem('autocomplete');

class Autocomplete extends React.Component {
  static propTypes = {
    ...reduxFormPropTypes.field,
    className: PropTypes.string,
    intl: intlShape,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.oneOfType([intlMessage, PropTypes.string]),
        labelProps: PropTypes.shape({}),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
    optionsFilter: PropTypes.func,
    placeholder: intlMessage,
    showErrorImmediately: PropTypes.bool,
  };

  static defaultProps = {
    options: [],
    optionsFilter: ({ label, value, isFilterable = true }, inputValue, intl) => {
      if (!isFilterable) return true;

      if (_isObject(label)) {
        label = intl.formatMessage(label);
      }

      return `${label} ${value}`.toLowerCase().includes(inputValue.toLowerCase());
    },
  };

  static getDerivedStateFromProps(props, state) {
    const {
      input: { value },
      options,
    } = props;
    const { hasOptions, filteredOptions, inputValue, selectedValue, highlightedValue } = state;

    // the field was updated outside of the component
    const valueHasChanged = selectedValue !== value;

    return {
      ...state,
      filteredOptions: !hasOptions || valueHasChanged ? options : filteredOptions,
      hasOptions: !!options.length,
      highlightedValue: valueHasChanged ? value : highlightedValue,
      inputValue: valueHasChanged ? null : inputValue,
      selectedValue: valueHasChanged ? value : selectedValue,
    };
  }

  constructor(props) {
    super(props);

    const {
      input: { value },
      options,
    } = props;

    this.inputRef = React.createRef();

    this.state = {
      inputValue: null,
      selectedValue: value,
      hasOptions: !!options.length,
      filteredOptions: options,
      highlightedValue: options.length ? options[0].value : null,
    };
  }

  setInputValue = eventOrValue => {
    const { options, optionsFilter, intl } = this.props;
    const { highlightedValue } = this.state;
    const inputValue = _isObject(eventOrValue) ? eventOrValue.target.value : eventOrValue;
    const filteredOptions =
      !inputValue || inputValue.trim() === ''
        ? options
        : options.filter(option => optionsFilter(option, inputValue, intl));
    const highlightedValueStillValid = filteredOptions.some(({ value }) => value === highlightedValue);

    let nextHighlightedValue;
    if (highlightedValueStillValid) {
      nextHighlightedValue = highlightedValue;
    } else if (filteredOptions.length > 0) {
      nextHighlightedValue = filteredOptions[0].value;
    } else {
      nextHighlightedValue = null;
    }

    this.setState({
      inputValue,
      filteredOptions,
      highlightedValue: nextHighlightedValue,
    });
  };

  resetInputValue = () => {
    this.setInputValue(null);
  };

  setSelected = value => {
    const {
      input: { onChange },
    } = this.props;
    onChange(value);
    this.resetInputValue();
    this.setState({ selectedValue: value });
  };

  handleInputFocus = ({ isOpen, toggleMenu }) => {
    if (!isOpen) toggleMenu();
  };

  handleInputClick = (event, { isOpen, toggleMenu }) => {
    event.stopPropagation();
    if (!isOpen) toggleMenu();
  };

  handleInputBlur = () => {
    const { highlightedValue } = this.state;
    const {
      input: { onChange },
      options,
      intl,
    } = this.props;

    onChange(highlightedValue);

    let selectedOptionLabel = options.find(({ value }) => value === highlightedValue)?.label;
    if (_isObject(selectedOptionLabel)) selectedOptionLabel = intl.formatMessage(selectedOptionLabel);
    this.setState({ selectedValue: highlightedValue, inputValue: selectedOptionLabel });
  };

  handleInputKeyDown = (event, { isOpen, closeMenu, toggleMenu, closeOnEsc }) => {
    const { filteredOptions, highlightedValue } = this.state;
    const key = event.key.toUpperCase();
    const highlightedIndex = _findIndex(filteredOptions, ({ value }) => value === highlightedValue);

    let nextIndex = null;
    if (key === 'ARROWDOWN') {
      nextIndex = highlightedIndex === filteredOptions.length - 1 ? 0 : highlightedIndex + 1;
    } else if (key === 'ARROWUP') {
      nextIndex = highlightedIndex === 0 ? filteredOptions.length - 1 : highlightedIndex - 1;
    }

    let nextHighlightedValue;
    if (nextIndex !== null) {
      nextHighlightedValue = filteredOptions.length === 0 ? null : filteredOptions[nextIndex].value;
    }

    if (key === 'ENTER') {
      event.preventDefault();
    }

    switch (key) {
      case 'ENTER':
        closeMenu();
        if (this.inputRef.current) this.inputRef.current.blur();
        if (highlightedValue) this.setSelected(highlightedValue);
        break;

      case 'TAB':
      case 'ESCAPE':
        this.resetInputValue();
        if (closeOnEsc) closeMenu();
        if (this.inputRef.current) this.inputRef.current.blur();
        break;

      case 'ARROWDOWN':
      case 'ARROWUP':
        event.preventDefault();
        if (!isOpen) toggleMenu();
        this.setState({ highlightedValue: nextHighlightedValue });
        break;

      default:
        if (!isOpen) toggleMenu();
        break;
    }
  };

  handleArrowOnClick = (event, { isOpen }) => {
    event.stopPropagation();

    if (!isOpen) {
      this.inputRef.current.focus();
    } else {
      this.inputRef.current.blur();
    }
  };

  handleItemSelect = ({ value }) => {
    this.setSelected(value);
  };

  renderToggle = toggleProps => {
    const { intl, placeholder, theme } = this.props;
    const { input, isOpen, toggleMenu, selectedValue, inputValue, setInputValue, options, disabled, hasError } =
      toggleProps;
    const selectedOption = options.find(({ value }) => value === selectedValue);

    let displayValue;
    if (inputValue !== null) {
      displayValue = inputValue;
    } else if (_isObject(selectedOption)) {
      displayValue = selectedOption.label;
    } else {
      displayValue = '';
    }

    let arrowTheme;
    if (disabled) {
      arrowTheme = 'disabled';
    } else {
      arrowTheme = 'dark';
    }

    return (
      <div
        className={element('toggle', { hasError, isDisabled: disabled, [`theme-${theme}`]: theme })}
        role="button"
        onKeyDown={_noop} /* appease jsx-a11y */
        tabIndex="-1" /* appease jsx-a11y */
        onClick={toggleMenu}>
        <input
          {...input}
          ref={this.inputRef}
          type="text"
          value={displayValue}
          onChange={setInputValue}
          onClick={event => this.handleInputClick(event, toggleProps)}
          onFocus={() => this.handleInputFocus(toggleProps)}
          onKeyDown={event => this.handleInputKeyDown(event, toggleProps)}
          onBlur={this.handleInputBlur}
          placeholder={!placeholder ? '' : intl.formatMessage(placeholder)}
          className={element('input')}
          disabled={disabled}
          autoComplete="off"
        />
        <SvgArrow
          className={element('caret')}
          direction={isOpen ? 'up' : 'down'}
          onClick={event => this.handleArrowOnClick(event, toggleProps)}
          theme={arrowTheme}
        />
      </div>
    );
  };

  renderContent = ({ filteredOptions, highlightedValue, isOpen, selectedValue }) => {
    filteredOptions = filteredOptions.length
      ? filteredOptions
      : filteredOptions.concat({
          value: '',
          label: messages.noMatch,
        });

    return (
      <Menu
        isOpen={isOpen}
        getKey="value"
        getText={item => item}
        items={filteredOptions}
        isSelected={({ value }) => value === selectedValue}
        isHighlighted={({ value }) => value === highlightedValue}
        renderMenuItem={({ label, labelProps = {} }) => {
          const filteredlabelProps = _omit(labelProps, ['onClick']);
          return _isString(label) ? label : <Message message={label} {...filteredlabelProps} />;
        }}
        onSelect={item => {
          const { labelProps = {} } = item;
          if (labelProps.onClick) {
            labelProps.onClick(item);
          } else {
            this.handleItemSelect(item);
          }
        }}
      />
    );
  };

  render() {
    const { meta, showErrorImmediately, className, ...restProps } = this.props;
    const hasError = shouldShowError(meta, showErrorImmediately);

    return (
      <div className={block(className)}>
        <Dropdown
          useOverlay={false} // lets the user click to reposition the cursor
          Toggle={this.renderToggle}
          Content={this.renderContent}
          // passed through to Toggle and Content to ensure rerenders
          {...restProps}
          {...this.state}
          setInputValue={this.setInputValue}
          hasError={hasError}
        />
        <InputError {...meta} showErrorImmediately={showErrorImmediately} />
      </div>
    );
  }
}

export default injectIntl(Autocomplete);
